import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { API } from '../lib/endpoints'
import type {
  Appointment,
  Operation,
  PatientFlow,
  PaginatedResponse
} from '../types/api'
import { format } from 'date-fns'

// Query keys factory
export const queryKeys = {
  appointments: {
    all: ['appointments'] as const,
    list: (params?: Record<string, string>) => [...queryKeys.appointments.all, 'list', params] as const,
    detail: (id: number) => [...queryKeys.appointments.all, 'detail', id] as const,
    today: () => [...queryKeys.appointments.all, 'today'] as const,
  },
  operations: {
    all: ['operations'] as const,
    list: (params?: Record<string, string>) => [...queryKeys.operations.all, 'list', params] as const,
    detail: (id: number) => [...queryKeys.operations.all, 'detail', id] as const,
    today: () => [...queryKeys.operations.all, 'today'] as const,
    dashboard: () => [...queryKeys.operations.all, 'dashboard'] as const,
  },
  patientFlow: {
    all: ['patientFlow'] as const,
    list: () => [...queryKeys.patientFlow.all, 'list'] as const,
    live: () => [...queryKeys.patientFlow.all, 'live'] as const,
  },
}

// ============= APPOINTMENTS =============

export function useTodayAppointments() {
  const today = format(new Date(), 'yyyy-MM-dd')
  return useQuery({
    queryKey: queryKeys.appointments.today(),
    queryFn: async (): Promise<Appointment[]> => {
      // Use same date-range approach as Calendar (start_date + end_date) for consistency.
      // Also request a high limit to avoid pagination cutting off results.
      const response = await api.get<PaginatedResponse<Appointment> | Appointment[]>(
        `${API.appointments.list}?start_date=${today}&end_date=${today}&limit=500`
      )
      // Handle both array and paginated response formats (same as useCalendar.ts)
      return Array.isArray(response) ? response : (response.results || [])
    },
    refetchInterval: 15_000,
  })
}

export function useAppointments(params?: Record<string, string>) {
  const searchParams = new URLSearchParams(params).toString()
  return useQuery({
    queryKey: queryKeys.appointments.list(params),
    queryFn: () => api.get<PaginatedResponse<Appointment>>(
      `${API.appointments.list}${searchParams ? '?' + searchParams : ''}`
    ),
  })
}

export function useAppointment(id: number) {
  return useQuery({
    queryKey: queryKeys.appointments.detail(id),
    queryFn: () => api.get<Appointment>(API.appointments.detail(id)),
    enabled: !!id,
  })
}

// ============= OPERATIONS =============

export function useTodayOperations() {
  const today = format(new Date(), 'yyyy-MM-dd')
  return useQuery({
    queryKey: queryKeys.operations.today(),
    queryFn: () => api.get<PaginatedResponse<Operation>>(
      `${API.operations.list}?date=${today}`
    ),
  })
}

export function useOpDashboard() {
  return useQuery({
    queryKey: queryKeys.operations.dashboard(),
    queryFn: () => api.get<Operation[]>(API.opDashboard.list),
  })
}

// ============= APPOINTMENT STATUS (FLOW) =============

export function useUpdateAppointmentStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.patch<Appointment>(API.appointments.status(id), { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all })
      queryClient.invalidateQueries({ queryKey: ['calendar-appointments'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.patientFlow.all })
      queryClient.invalidateQueries({ queryKey: ['waiting-display'] })
    },
  })
}

// PATCH /api/appointments/{id}/ with resource_ids — used by the dashboard
// flow-card room dropdown to attach a room to a roomless live appointment.
export function useAssignAppointmentResources() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, resourceIds }: { id: number; resourceIds: number[] }) =>
      api.patch<Appointment>(API.appointments.detail(id), { resource_ids: resourceIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all })
      queryClient.invalidateQueries({ queryKey: ['calendar-appointments'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.patientFlow.all })
    },
  })
}

// ============= PATIENT FLOW =============

export function usePatientFlowLive() {
  return useQuery({
    queryKey: queryKeys.patientFlow.live(),
    queryFn: () => api.get<PatientFlow[]>(API.patientFlow.live),
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  })
}

export function useUpdatePatientFlowStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.patch<PatientFlow>(API.patientFlow.status(id), { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.patientFlow.all })
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all })
      queryClient.invalidateQueries({ queryKey: ['waiting-display'] })
    },
  })
}
